import styled, { css } from 'styled-components';
import Pagination from '../../components/pagination/Pagination';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Select from 'react-select';
import GameService from '../../../service/game/GameService';
import InterestService from '../../../service/Interest';
import { isLogin } from '../../../utils/auth';
import AddInterestItemModal from '../../components/modal/AddInterestItem';

interface LankSelectProps {
  isSelected?: boolean;
}
interface GameEventProps {
  gameId: number;
}

const GameEvent = ({ gameId }: GameEventProps) => {
  const navigate = useNavigate();
  const [sortOption, setSortOption] = useState<string>('우승 많은순');
  const [rankingList, setRankingList] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedIdx, setSelectedIdx] = useState<number | null>(null);
  const [showLikeModal, setShowLikeModal] = useState(false);
  const [expandedTags, setExpandedTags] = useState<{ [key: number]: boolean }>(
    {},
  );
  const sortOptions = [
    { value: '우승 많은순', label: '우승 많은순' },
    { value: '우승 적은순', label: '우승 적은순' },
    { value: '낮은 가격순', label: '낮은 가격순' },
    { value: '높은 가격순', label: '높은 가격순' },
  ];
  const sortedData = useMemo(() => {
    const arr = [...rankingList];

    const safeNum = (v: any) =>
      isNaN(Number(v)) || v === null || v === undefined ? 0 : Number(v);

    switch (sortOption) {
      case '우승 많은순':
        arr.sort((a, b) => safeNum(b.win_cnt) - safeNum(a.win_cnt));
        break;
      case '우승 적은순':
        arr.sort((a, b) => safeNum(a.win_cnt) - safeNum(b.win_cnt));
        break;
      case '낮은 가격순':
        arr.sort((a, b) => safeNum(a.dealPrice) - safeNum(b.dealPrice));
        break;
      case '높은 가격순':
        arr.sort((a, b) => safeNum(b.dealPrice) - safeNum(a.dealPrice));
        break;
    }

    return arr;
  }, [sortOption, rankingList]);
  const priceToNumber = (p?: string) => {
    if (!p) return 0;
    const n = parseFloat(p.replace(/[^\d.]/g, '')) || 0;
    return p.includes('억') ? n * 100_000_000 : n;
  };

  // react-select styles
  const customStyles = {
    control: (provided: any) => ({
      ...provided,
      borderRadius: '8px',
      height: '36px',
      fontSize: '14px',
      fontWeight: 500,
      Width: '120px',
      display: 'flex',
      flexDirection: 'row',
      backgroundColor: 'rgba(2, 32, 71, 0.05)',
      border: 'none',
      ':hover': {
        background: 'rgba(0, 27, 55, 0.10)',
      },
      boxShadow: 'none',
    }),
    indicatorsContainer: (provided: any) => ({
      ...provided,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 0,
      color: '#B0B8C1',
    }),
    singleValue: (provided: any) => ({
      ...provided,
      color: 'rgba(0, 12, 30, 0.80)',
      fontSize: '14px',
      display: 'flex',
      alignItems: 'center',
      padding: '0 0 0 14px',
    }),
    dropdownIndicator: (provided: any) => ({
      ...provided,
      color: '#B0B8C1',
      padding: '0 14px 0 0',
    }),
    indicatorSeparator: () => ({
      display: 'none',
    }),
    valueContainer: (provided: any) => ({
      ...provided,
      padding: 0,
    }),
    menu: (provided: any) => ({
      ...provided,
      borderRadius: '12px',
      padding: '6px',
      boxShadow: '0 2px 10px 0 rgba(0, 0, 0, 0.12)',
      border: '1px solid E1E2E4',
      overflow: 'hidden',
    }),
  };

  // 커스텀 옵션: 선택된 항목에만 체크 아이콘
  const CustomOption = (props: any) => {
    const { isSelected, innerRef, innerProps, data } = props;
    return (
      <LankSelect ref={innerRef} {...innerProps}>
        <span>{data.label}</span>
        {isSelected && (
          <img
            src="/assets/images/game/icon_select_check.svg"
            alt="selected"
            width={16}
            height={16}
          />
        )}
      </LankSelect>
    );
  };
  const pageSize = 5;
  const [currentPage, setCurrentPage] = useState(1);

  const toNum = (v?: string | number) =>
    Number(String(v ?? '0').replace(/[^0-9.-]/g, '')) || 0;

  const getPriceValue = (row: any) =>
    typeof row?.minPrice === 'number'
      ? row.minPrice
      : priceToNumber(row?.price);

  useEffect(() => {
    const fetchRanking = async () => {
      try {
        setLoading(true);
        const res = await GameService.getRanking(Number(gameId));
        setRankingList(res.data || []);
      } catch (error) {
        console.error('랭킹 불러오기 실패:', error);
      } finally {
        setLoading(false);
      }
    };

    if (gameId) {
      fetchRanking();
    }
  }, [gameId]);
  const top3IidxList = useMemo(() => {
    return [...rankingList]
      .sort((a, b) => toNum(b.win_cnt) - toNum(a.win_cnt))
      .slice(0, 3)
      .map((item) => item.iidx);
  }, [rankingList]);

  const totalPages = Math.ceil(sortedData.length / pageSize);

  const paginatedData = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return sortedData.slice(start, start + pageSize);
  }, [sortedData, currentPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [sortOption]);

  const toggleTags = (index: number) => {
    setExpandedTags((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };
  const handleDisLikeItem = async (iidx: number) => {
    try {
      await InterestService.dislike(iidx);
      alert('관심물건에서 삭제되었습니다.');

      setRankingList((prev) =>
        prev.map((item) =>
          (item.iidx || item.itemId) === iidx
            ? { ...item, is_like: false }
            : item,
        ),
      );
    } catch (error) {
      console.error('관심물건 삭제 실패:', error);
      alert('관심물건 삭제 중 오류가 발생했습니다.');
    }
  };
  const handleLikeClick = (item: any) => {
    if (!isLogin()) {
      alert('로그인이 필요합니다.');
      navigate('/login');
      return;
    }

    if (item.is_like) {
      handleDisLikeItem(item.iidx || item.itemId);
    } else {
      setSelectedIdx(item.iidx || item.itemId);
      setShowLikeModal(true);
    }
  };
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
  const toggleExpand = (key: number) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };
  const renderTags = (
    status: string,
    tags: string[],
    key: number,
    max: number = 2,
  ) => {
    const combined = [
      ...(status ? [`__STATUS__${status}`] : []),
      ...(tags ?? []),
    ];
    const isExpanded = expandedRows.has(key);
    const visible = isExpanded ? combined : combined.slice(0, max);
    const hiddenCount = isExpanded ? 0 : Math.max(combined.length - max, 0);

    return (
      <>
        {visible.map((tag, i) =>
          tag.startsWith('__STATUS__') ? (
            <TagRed key={`${key}-${i}`}>{tag.replace('__STATUS__', '')}</TagRed>
          ) : tag.includes('대항력') ? (
            <TagBlue key={`${key}-${i}`}>{tag}</TagBlue>
          ) : (
            <TagGray key={`${key}-${i}`}>{tag}</TagGray>
          ),
        )}
        {hiddenCount > 0 && (
          <TagGray
            onClick={(e) => {
              e.stopPropagation();
              toggleExpand(key);
            }}
            style={{ cursor: 'pointer' }}
          >
            +{hiddenCount}개
          </TagGray>
        )}
        {isExpanded && (
          <TagGray
            onClick={(e) => {
              e.stopPropagation();
              toggleExpand(key);
            }}
            style={{ cursor: 'pointer' }}
          >
            접기
          </TagGray>
        )}
      </>
    );
  };
  return (
    <TableList>
      <SelectBox>
        <Select
          options={sortOptions}
          value={sortOptions.find((o) => o.value === sortOption)}
          onChange={(opt) => setSortOption(opt?.value || '추천순')}
          styles={customStyles}
          isSearchable={false}
          components={{ Option: CustomOption }}
        />
      </SelectBox>

      {paginatedData.map((item, index) => {
        const rankIndex = (currentPage - 1) * pageSize + index + 1;
        const rankStyle =
          top3IidxList.indexOf(item.iidx) !== -1
            ? top3IidxList.indexOf(item.iidx) + 1
            : undefined;

        return (
          <TableItem key={item.iidx} $rank={rankStyle}>
            <Lable $rank={rankStyle}>
              <span>{item.win_cnt || 0}회 우승</span>
              <img
                src={`/assets/images/game/${item.is_like ? 'heart_on' : 'heart_off'}.svg`}
                alt="like"
                onClick={(e) => {
                  e.stopPropagation();
                  handleLikeClick(item);
                }}
              />
            </Lable>
            <InfoBox>
              <TitleInfo>
                <Rank>
                  {rankStyle ? (
                    <img
                      src={`/assets/images/icon/lank_medal_${rankStyle}.svg`}
                      alt={`메달 ${rankStyle}`}
                    />
                  ) : (
                    <RankIndex>{rankIndex}</RankIndex>
                  )}
                </Rank>
                {item.address || ''}
              </TitleInfo>
              <FlexBox02>
                <Local>
                  <span>{item.category}</span>
                  <Cicle>・</Cicle>
                  {item.address}
                  <Cicle>・</Cicle>
                  {(Number(item.dealPrice) / 100000000).toFixed(1)} 억원
                  <Cicle>・</Cicle>
                  {item.buildingArea}m² ({Math.round(item.buildingArea / 3.3)}
                  평)
                </Local>
              </FlexBox02>
              <TagWrap>
                {renderTags(item.status, item.tag || [], item.iidx, 2)}
              </TagWrap>
            </InfoBox>

            <MoveBtn
              rankIndex={rankIndex}
              onClick={() => {
                window.open(`/auction/detail/${item.iidx}`, '_blank');
              }}
            >
              경매 물건 상세보기
              <img
                src={`/assets/images/game/${
                  rankIndex === 3 ? 'move_icon_white' : 'move_icon'
                }.svg`}
              />
            </MoveBtn>
          </TableItem>
        );
      })}
      <PageWrap>
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={(page) => setCurrentPage(page)}
        />
      </PageWrap>
      {showLikeModal && selectedIdx !== null && (
        <AddInterestItemModal
          setShowLikeModal={setShowLikeModal}
          selectedIdx={selectedIdx}
          mode="add"
          selectedItem={{
            iidx: selectedIdx,
            group: '',
            group_idx: 0,
            level: 0,
            memo: '',
          }}
          setActiveItem={() => {}}
          checkIsLikeItem={(iidx: number) => {
            setRankingList((prev) =>
              prev.map((item) =>
                item.iidx === iidx ? { ...item, is_like: true } : item,
              ),
            );
          }}
        />
      )}
    </TableList>
  );
};

export default GameEvent;

const TableList = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;
  align-self: stretch;
`;

const TableItem = styled.div<{ $rank?: number }>`
  display: flex;
  padding: 16px;
  align-items: start;
  gap: 12px;
  align-self: stretch;
  border: 1px solid #e1e2e4;
  background: #fff;
  border-radius: 12px;
  flex-direction: column;
  ${({ $rank }) =>
    $rank === 1 &&
    css`
      border: 1px solid #ffc803;
      background: rgba(255, 200, 3, 0.08);
    `}
  ${({ $rank }) =>
    $rank === 2 &&
    css`
      border: 1px solid #9cc2e4;
      background: rgba(156, 194, 228, 0.12);
    `}
  ${({ $rank }) =>
    $rank === 3 &&
    css`
      border: 1px solid #e3b59f;
      background: rgba(227, 181, 159, 0.12);
    `}
`;

const Rank = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  width: 20px;
  height: 20px;
  > img {
    width: 20px;
    height: 20px;
  }
  > span {
    color: #333d4b;

    font-family: 'Spoqa Han Sans Neo';
    font-size: 14px;
    font-style: normal;
    font-weight: 700;
    line-height: 145%; /* 20.3px */
    letter-spacing: -0.28px;
  }
`;

const InfoBox = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: start;
  gap: 8px;
  flex: 1 0 0;
  width: 100%;
  > div {
    flex-direction: row;
    display: flex;
    flex-wrap: wrap;
  }
`;

const TitleInfo = styled.div`
  display: flex;
  color: #333d4b;
  font-size: 14px;
  flex-direction: row;
  align-items: center;
  font-style: normal;
  font-weight: 700;
  gap: 2px;
  letter-spacing: -0.32px;
  > span {
    color: #008aff;
    font-weight: 700;
  }
`;

const Local = styled.div`
  color: #6b7684;
  font-family: 'Spoqa Han Sans Neo';
  font-size: 12px;
  font-style: normal;
  font-weight: 500;
  line-height: 145%; /* 17.4px */
  letter-spacing: -0.24px;
  display: flex;
  flex-wrap: wrap;
  span {
    color: #333d4b;
    font-family: 'Spoqa Han Sans Neo';
    font-size: 12px;
    font-style: normal;
    font-weight: 700;
    line-height: 145%; /* 17.4px */
    letter-spacing: -0.24px;
  }
`;
const Cicle = styled.div`
  color: #e1e2e4;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  letter-spacing: -0.28px;
`;

const TagWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-direction: row;
  align-self: stretch;
`;

const TagRed = styled.div`
  display: flex;
  padding: 2px 5px;
  justify-content: center;
  align-items: center;
  border-radius: 4px;
  background: #fff0f0;
  color: #f04452;

  font-size: 12px;
  font-style: normal;
  font-weight: 500;
  line-height: 145%; /* 17.4px */
  letter-spacing: -0.24px;
`;

const TagBlue = styled.div`
  display: flex;
  padding: 2px 5px;
  justify-content: center;
  align-items: center;
  border-radius: 4px;
  background: #e7f0fe;
  color: #008aff;

  font-size: 12px;
  font-style: normal;
  font-weight: 500;
  line-height: 145%; /* 17.4px */
  letter-spacing: -0.24px;
`;

const TagGray = styled.div`
  display: flex;
  padding: 2px 5px;
  justify-content: center;
  align-items: center;
  border-radius: 4px;
  background: #f4f5f9;

  color: #4e5968;
  font-size: 12px;
  font-style: normal;
  font-weight: 500;
  line-height: 145%; /* 17.4px */
  letter-spacing: -0.24px;
`;

const Lable = styled.div<{ $rank?: number }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 4px;
  width: 100%;
  span {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 118px;
    height: 28px;
    background: url(/assets/images/game/label_event_gray.svg) no-repeat center;
    background-size: cover;
    font-size: 14px;
    font-style: normal;
    font-weight: 500;
    line-height: 145%; /* 20.3px */
    letter-spacing: -0.28px;
    color: #4e5968;
    ${({ $rank }) =>
      $rank === 1 &&
      css`
        background: url(/assets/images/game/label_event_gold.svg) no-repeat
          center;
        color: #fff;
      `}
    ${({ $rank }) =>
      $rank === 2 &&
      css`
        background: url(/assets/images/game/label_event_blue.svg) no-repeat
          center;
        color: #fff;
      `}
  ${({ $rank }) =>
      $rank === 3 &&
      css`
        background: url(/assets/images/game/label_event_brown.svg) no-repeat
          center;
        color: #fff;
      `}
  }
`;

interface MoveBtnProps {
  rankIndex: number;
}

const MoveBtn = styled.div<MoveBtnProps>`
  display: flex;
  padding: 4px 8px;
  justify-content: center;
  align-items: center;
  gap: 5px;
  align-self: stretch;
  flex-direction: row;
  color: rgba(0, 12, 30, 0.8);
  border-radius: 8px;
  font-family: 'Spoqa Han Sans Neo';
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 145%; /* 20.3px */
  letter-spacing: -0.28px;
  ${({ rankIndex }) => {
    if (rankIndex === 1) {
      return `
        background: #FFC803;
      `;
    } else if (rankIndex === 2) {
      return `
        background: #9CC2E4;
      `;
    } else if (rankIndex === 3) {
      return `
        background: #B6917F; 
        color: #fff;
      `;
    } else {
      return `
        background: #E8ECEF;
      `;
    }
  }}
`;
const PageWrap = styled.div`
  flex-direction: row;
  justify-content: center;
  width: 100%;
  > div {
    margin: 0;
  }
`;

const Result = styled.div`
  border-radius: 4px;
  background: rgba(2, 32, 71, 0.05);
  display: flex;
  padding: 4px 12px;
  justify-content: center;
  align-items: center;
  color: #4e5968;

  /* Re_SPOQA/body/med_14 */
  font-family: 'Spoqa Han Sans Neo';
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 145%; /* 20.3px */
  letter-spacing: -0.28px;
`;
const LankSelect = styled.div<LankSelectProps>`
  display: flex;
  flex-direction: row;
  padding: 6px 8px 6px 6px;
  gap: 8px;
  align-items: center;
  cursor: pointer;
  border-radius: 8px;

  &:hover {
    background: rgba(2, 32, 71, 0.05);
  }
  span {
    color: ${({ isSelected }) =>
      isSelected ? 'rgba(0, 12, 30, 0.80)' : 'rgba(3, 18, 40, 0.7)'};
    font-size: 14px;
    font-weight: 500;
    line-height: 145%; /* 20.3px */
    letter-spacing: -0.28px;
  }

  img {
    width: 16px;
    height: 16px;
  }
`;
const SelectBox = styled.div`
  width: 118px;
  margin-top: 24px;
  margin-bottom: 20px;
`;
const RankIndex = styled.div`
  display: flex;
  width: 20px;
  height: 20px;
  justify-content: center;
  align-items: center;
  gap: 10px;
  border-radius: 999px;
  background: #f6f7f9;
  color: #6b7684;
  font-family: 'Spoqa Han Sans Neo';
  font-size: 12px;
  font-style: normal;
  font-weight: 700;
  line-height: 145%; /* 17.4px */
  letter-spacing: -0.24px;
`;
const FlexBox02 = styled.div`
  display: flex;
  gap: 4px;
  flex-direction: column;
`;
